import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Target, Plus, Pencil, X, TrendingUp, ShieldCheck, Leaf, Award } from "lucide-react";
import { toast } from "sonner";

const CAT_META = {
  safety: { label: "Safety", icon: ShieldCheck, tone: "border-rose-200", head: "text-rose-700 bg-rose-50" },
  environment: { label: "Environment", icon: Leaf, tone: "border-emerald-200", head: "text-emerald-700 bg-emerald-50" },
  quality: { label: "Quality", icon: Award, tone: "border-sky-200", head: "text-sky-700 bg-sky-50" },
};
const STATUS_META = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  on_track: { label: "On track", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  at_risk: { label: "At risk", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  off_track: { label: "Off track", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  achieved: { label: "Achieved", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

export default function ObjectivesTab() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);       // objective form
  const [progress, setProgress] = useState(null); // {id, objective, status, current_value, comment, updates}

  const load = async () => {
    try { setData((await api.get("/hseq-objectives")).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Objectives load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.objective?.trim()) { toast.error("Objective is required"); return; }
    try {
      await api.post("/hseq-objectives", modal);
      toast.success("Objective saved"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (o) => {
    if (!confirm(`Remove objective "${o.objective}"?`)) return;
    try { await api.delete(`/hseq-objectives/${o.id}`); toast.success("Objective removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveProgress = async () => {
    try {
      await api.post(`/hseq-objectives/${progress.id}/progress`, {
        status: progress.status, current_value: progress.current_value, comment: progress.comment });
      toast.success("Progress recorded"); setProgress(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading objectives…</div>;
  }
  const canEdit = data.can_edit;

  return (
    <div className="space-y-4" data-testid="hseq-objectives-tab">
      <div className="flex items-start gap-2">
        <Target className="w-4 h-4 text-gbe mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">Quality & Planning Objectives</div>
          <div className="text-[11px] text-slate-500">{data.source}</div>
        </div>
        {canEdit && (
          <Button size="sm" className="h-8 text-xs bg-gbe hover:bg-gbe-dark text-white" data-testid="obj-add"
                  onClick={() => setModal({ category: "quality", objective: "", measure: "", target: "", owner: "" })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add objective</Button>
        )}
      </div>

      {Object.keys(CAT_META).map((cat) => {
        const meta = CAT_META[cat];
        const items = data.items.filter((o) => o.category === cat);
        const Icon = meta.icon;
        return (
          <div key={cat} className={`rounded-lg border ${meta.tone} bg-white overflow-hidden`} data-testid={`obj-section-${cat}`}>
            <div className={`px-4 py-2 flex items-center gap-2 text-xs font-semibold ${meta.head}`}>
              <Icon className="w-3.5 h-3.5" /> {meta.label}
              <span className="font-normal opacity-70">· {items.length} objective{items.length !== 1 ? "s" : ""}</span>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50/60 border-b border-slate-100 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-1.5">Objective</th>
                  <th className="text-left px-3 py-1.5">Measure</th>
                  <th className="text-left px-3 py-1.5">Target</th>
                  <th className="text-left px-3 py-1.5">Current</th>
                  <th className="text-left px-3 py-1.5">Owner</th>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/40 align-top" data-testid={`obj-row-${o.id}`}>
                    <td className="px-4 py-2 text-slate-800 font-medium max-w-[260px]">{o.objective}</td>
                    <td className="px-3 py-2 text-slate-500">{o.measure || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">{o.target || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {o.current_value || <span className="text-slate-300">—</span>}
                      {o.live && (
                        <div className="mt-0.5" title={`${o.live.detail} · as of ${(o.live.as_of || "").slice(0, 10)}`}
                             data-testid={`obj-live-${o.id}`}>
                          <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            LIVE {o.live.value}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{o.owner || <span className="text-slate-300">unassigned</span>}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_META[o.status]?.cls}`}>{STATUS_META[o.status]?.label || o.status}</Badge>
                      {o.updates?.length > 0 && (
                        <div className="text-[9px] text-slate-400 mt-0.5">last: {(o.updates[o.updates.length - 1].at || "").slice(0, 10)}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {canEdit && (
                        <>
                          <button className="text-slate-400 hover:text-gbe mr-1.5" title="Record progress"
                                  data-testid={`obj-progress-${o.id}`}
                                  onClick={() => setProgress({ ...o, comment: "" })}>
                            <TrendingUp className="w-3.5 h-3.5" /></button>
                          <button className="text-slate-300 hover:text-slate-600 mr-1.5" title="Edit"
                                  data-testid={`obj-edit-${o.id}`}
                                  onClick={() => setModal({ id: o.id, category: o.category, objective: o.objective, measure: o.measure || "", target: o.target || "", owner: o.owner || "" })}>
                            <Pencil className="w-3 h-3" /></button>
                          <button className="text-slate-300 hover:text-rose-600" title="Remove"
                                  data-testid={`obj-delete-${o.id}`} onClick={() => remove(o)}>
                            <X className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={7} className="px-4 py-4 text-center text-slate-400 text-[11px]">No objectives in this category.</td></tr>}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Objective modal */}
      {modal && (
        <Overlay onClose={() => setModal(null)} title={modal.id ? "Edit objective" : "Add objective"} testid="obj-modal">
          <label className="block text-xs mb-3"><span className="text-slate-600">Category</span>
            <Select value={modal.category} onValueChange={(v) => setModal({ ...modal, category: v })}>
              <SelectTrigger className="h-8 mt-1 text-xs" data-testid="obj-f-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(CAT_META).map((c) => <SelectItem key={c} value={c}>{CAT_META[c].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block text-xs mb-3"><span className="text-slate-600">Objective</span>
            <Input value={modal.objective} data-testid="obj-f-objective" className="h-8 mt-1"
                   onChange={(e) => setModal({ ...modal, objective: e.target.value })} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs mb-3"><span className="text-slate-600">Measure</span>
              <Input value={modal.measure} data-testid="obj-f-measure" className="h-8 mt-1"
                     onChange={(e) => setModal({ ...modal, measure: e.target.value })} /></label>
            <label className="block text-xs mb-3"><span className="text-slate-600">Target</span>
              <Input value={modal.target} data-testid="obj-f-target" className="h-8 mt-1"
                     onChange={(e) => setModal({ ...modal, target: e.target.value })} /></label>
          </div>
          <label className="block text-xs mb-3"><span className="text-slate-600">Owner (name or email)</span>
            <Input value={modal.owner} data-testid="obj-f-owner" className="h-8 mt-1"
                   onChange={(e) => setModal({ ...modal, owner: e.target.value })} /></label>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={save} data-testid="obj-save">Save</Button>
          </div>
        </Overlay>
      )}

      {/* Progress modal */}
      {progress && (
        <Overlay onClose={() => setProgress(null)} title="Record progress" testid="obj-progress-modal">
          <div className="text-xs text-slate-600 mb-3">{progress.objective} <span className="text-slate-400">(target {progress.target || "—"})</span></div>
          {progress.live && (
            <div className="mb-3 flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
              <span className="text-emerald-700 font-semibold">Live from hub data: {progress.live.value}</span>
              <span className="text-emerald-600/70 flex-1 truncate" title={progress.live.detail}>{progress.live.detail}</span>
              <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-300" data-testid="obj-p-use-live"
                      onClick={() => setProgress({ ...progress, current_value: progress.live.value })}>Use</Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs mb-3"><span className="text-slate-600">Status</span>
              <Select value={progress.status} onValueChange={(v) => setProgress({ ...progress, status: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="obj-p-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block text-xs mb-3"><span className="text-slate-600">Current value</span>
              <Input value={progress.current_value || ""} placeholder="e.g. 96%" data-testid="obj-p-value" className="h-8 mt-1"
                     onChange={(e) => setProgress({ ...progress, current_value: e.target.value })} /></label>
          </div>
          <label className="block text-xs mb-3"><span className="text-slate-600">Comment</span>
            <Textarea rows={2} value={progress.comment} data-testid="obj-p-comment" className="text-xs mt-1"
                      onChange={(e) => setProgress({ ...progress, comment: e.target.value })} /></label>
          {progress.updates?.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto border-l-2 border-slate-100 pl-2 space-y-1">
              {progress.updates.slice().reverse().map((u, i) => (
                <div key={i} className="text-[10px] text-slate-500">
                  <span className="text-slate-400">{(u.at || "").slice(0, 10)}</span>{" "}
                  <span className="font-medium">{STATUS_META[u.status]?.label || u.status}</span>
                  {u.value && <span> · {u.value}</span>} <span className="text-slate-400">by {u.by}</span>
                  {u.comment && <span className="italic text-slate-400"> — {u.comment}</span>}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setProgress(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={saveProgress} data-testid="obj-p-save">Save progress</Button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mt-16 p-5" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
